/**
 * ImportTab Component
 * Manages data import functionality (households, players, coaches)
 */
import "./ImportTab.css";
import { Button } from "../../components/ui/button";
import { 
  Users, User, UserCheck, Upload, Download, History, 
  FileSpreadsheet, CheckCircle, AlertCircle, Loader2 
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const IMPORT_TYPES = [
  {
    id: "households",
    title: "Households & Players",
    description: "Import families with parents and their children. Parents will receive welcome emails with login credentials.",
    icon: Users,
    color: "bg-blue-100 text-blue-600",
    fields: ["Parent Name", "Email", "Phone", "Address", "Player Name", "DOB", "Gender"]
  },
  {
    id: "players",
    title: "Players Only",
    description: "Import players and optionally link them to existing parent accounts by email.",
    icon: User,
    color: "bg-emerald-100 text-emerald-600",
    fields: ["First Name", "Last Name", "DOB", "Gender", "Parent Email", "Medical Notes"]
  },
  {
    id: "coaches",
    title: "Coaches & Staff",
    description: "Import coaches, assistants, and team managers. They will receive invitation emails.",
    icon: UserCheck,
    color: "bg-purple-100 text-purple-600",
    fields: ["First Name", "Last Name", "Email", "Phone", "Role", "Certifications"]
  }
];

const ImportTab = ({ 
  token,
  API,
  importHistory, 
  loadingImportHistory,
  setSelectedImportType,
  setImportWizardOpen
}) => {
  const handleDownloadTemplate = async (typeId) => {
    try {
      const response = await axios.get(`${API}/import/template/${typeId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${typeId}_import_template.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Template downloaded");
    } catch (error) {
      toast.error("Failed to download template");
    }
  };

  const handleImport = (typeId) => {
    setSelectedImportType(typeId);
    setImportWizardOpen(true);
  };

  const getImportTypeLabel = (importType) => {
    const type = IMPORT_TYPES.find(t => t.id === importType);
    return type ? type.title : importType;
  };

  return (
    <div className="space-y-6" data-testid="import-tab">
      {/* Import Type Cards */}
      <div className="grid gap-4">
        {IMPORT_TYPES.map((type) => {
          const Icon = type.icon;
          return (
            <div 
              key={type.id}
              className="bg-white border border-slate-200/60 rounded-2xl p-5"
            >
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-xl ${type.color} shrink-0`}>
                  <Icon className="w-6 h-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-900">{type.title}</h3>
                      <p className="text-sm text-slate-500 mt-0.5">{type.description}</p>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {type.fields.slice(0, 4).map((field, i) => (
                          <span 
                            key={i}
                            className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-md"
                          >
                            {field}
                          </span>
                        ))}
                        {type.fields.length > 4 && (
                          <span className="px-2 py-0.5 bg-slate-100 text-slate-500 text-xs rounded-md">
                            +{type.fields.length - 4} more
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownloadTemplate(type.id)}
                        className="rounded-xl"
                      >
                        <Download className="w-4 h-4 mr-1.5" />
                        Template
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleImport(type.id)}
                        className="rounded-xl bg-slate-900 hover:bg-slate-800"
                        data-testid={`import-${type.id}-btn`}
                      >
                        <Upload className="w-4 h-4 mr-1.5" />
                        Import
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Import History */}
      <div className="bg-white border border-slate-200/60 rounded-2xl overflow-hidden">
        <div className="p-5 border-b border-slate-100 flex items-center gap-3">
          <History className="w-5 h-5 text-slate-400" />
          <h3 className="font-semibold text-slate-900">Import History</h3>
        </div>
        
        {loadingImportHistory ? (
          <div className="p-8 text-center text-slate-500">
            <Loader2 className="w-8 h-8 animate-spin mx-auto" />
          </div>
        ) : importHistory.length === 0 ? (
          <div className="p-8 text-center">
            <FileSpreadsheet className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No imports yet</p>
            <p className="text-sm text-slate-400 mt-1">Your import history will appear here</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {importHistory.slice(0, 10).map((record) => (
              <div key={record.import_id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                <div className="flex items-center gap-4">
                  <div className={`p-2 rounded-lg ${
                    record.error_count === 0 ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'
                  }`}>
                    {record.error_count === 0 ? (
                      <CheckCircle className="w-5 h-5" />
                    ) : (
                      <AlertCircle className="w-5 h-5" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">
                      {getImportTypeLabel(record.import_type)}
                    </p>
                    <p className="text-sm text-slate-500">
                      {record.imported_count} imported
                      {record.skipped_count > 0 && `, ${record.skipped_count} skipped`}
                      {record.error_count > 0 && `, ${record.error_count} errors`}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-500">
                    {new Date(record.created_at).toLocaleDateString('en-US', {
                      month: 'short', day: 'numeric', year: 'numeric'
                    })}
                  </p>
                  {record.send_emails && (
                    <p className="text-xs text-slate-400">Emails sent</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tips */}
      <div className="bg-slate-900 text-white rounded-2xl p-6">
        <h3 className="font-semibold mb-4">Tips for Successful Imports</h3>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-sm font-medium shrink-0">1</div>
            <div>
              <p className="font-medium">Use the Template</p>
              <p className="text-sm text-slate-300 mt-1">Download our CSV template to ensure your data is formatted correctly.</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-sm font-medium shrink-0">2</div>
            <div>
              <p className="font-medium">Check for Duplicates</p>
              <p className="text-sm text-slate-300 mt-1">Existing emails will be linked to current accounts.</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-sm font-medium shrink-0">3</div>
            <div>
              <p className="font-medium">Preview First</p>
              <p className="text-sm text-slate-300 mt-1">Review your data and fix any errors before importing.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportTab;
